import type { MatchRecordV1 } from "../match/persistence";
import { parseMatchRecord } from "../match/persistence";

export const SHARED_MATCH_HASH_PREFIX = "#passo=";
export const MAX_SHARED_MATCH_HASH_LENGTH = 250_000;

type SharedMatchEnvelopeV1 = Readonly<{
  version: 1;
  step: number;
  record: MatchRecordV1;
}>;

export type SharedMatchParseResult =
  | Readonly<{ kind: "none" }>
  | Readonly<{ kind: "invalid"; message: string }>
  | Readonly<{ kind: "valid"; record: MatchRecordV1; step: number }>;

export function createSharedMatchHash(record: MatchRecordV1, step: number): string {
  if (!Number.isSafeInteger(step) || step < 0 || step > record.moves.length) {
    throw new RangeError("Shared match step is outside the replay timeline.");
  }
  const envelope: SharedMatchEnvelopeV1 = Object.freeze({ version: 1, step, record });
  const encoded = bytesToBase64Url(new TextEncoder().encode(JSON.stringify(envelope)));
  const hash = `${SHARED_MATCH_HASH_PREFIX}${encoded}`;
  if (hash.length > MAX_SHARED_MATCH_HASH_LENGTH) {
    throw new RangeError("This match is too long to share reliably as a URL.");
  }
  return hash;
}

export function createSharedMatchUrl(
  record: MatchRecordV1,
  step: number,
  location: Pick<Location, "href"> = globalThis.location,
): string {
  const url = new URL(location.href);
  url.hash = createSharedMatchHash(record, step).slice(1);
  return url.toString();
}

export function parseSharedMatchHash(hash: string): SharedMatchParseResult {
  if (!hash.startsWith(SHARED_MATCH_HASH_PREFIX)) return Object.freeze({ kind: "none" });
  if (hash.length > MAX_SHARED_MATCH_HASH_LENGTH)
    return invalid("The shared match link is too long.");

  let value: unknown;
  try {
    const encoded = hash.slice(SHARED_MATCH_HASH_PREFIX.length);
    if (encoded.length === 0 || !/^[A-Za-z0-9_-]+$/u.test(encoded)) throw new Error("encoding");
    value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(base64UrlToBytes(encoded)));
  } catch {
    return invalid("The shared match link is damaged or incomplete.");
  }

  if (
    !isRecord(value) ||
    !hasExactlyKeys(value, ["version", "step", "record"]) ||
    value["version"] !== 1 ||
    !Number.isSafeInteger(value["step"])
  ) {
    return invalid("The shared match link uses an unsupported format.");
  }
  const parsed = parseMatchRecord(value["record"]);
  if (!parsed.ok) return invalid(`The shared match is invalid. ${parsed.message}`);
  const step = Number(value["step"]);
  if (step < 0 || step > parsed.record.moves.length) {
    return invalid("The highlighted turn does not exist in this match.");
  }
  return Object.freeze({ kind: "valid", record: parsed.record, step });
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 8192;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function base64UrlToBytes(value: string): Uint8Array {
  const standard = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = standard.padEnd(Math.ceil(standard.length / 4) * 4, "=");
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function invalid(message: string): SharedMatchParseResult {
  return Object.freeze({ kind: "invalid", message });
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(
  record: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
